import React from 'react';
import PropTypes from 'prop-types';
import { OrderedMap, Set, Map } from 'immutable';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import VisibilitySensor from 'react-visibility-sensor';

import RequiredField from 'components/common/required-field';

import { INPUT_IMPRESSION, INPUT_FOCUS, INPUT_CLICK, INPUT_CHANGE } from 'lib/analytics/events';

const propTypes = {
  'id': PropTypes.string.isRequired,
  'labelClassName': PropTypes.string,
  'placeholderText': PropTypes.string,
  'inputType': PropTypes.string,
  'onFieldEvent': PropTypes.func.isRequired,
  'setShouldScrollToError': PropTypes.func.isRequired,
  'modifyRequiredFieldList': PropTypes.func.isRequired,
  'className': PropTypes.string,
  'requiredFields': PropTypes.instanceOf(Set).isRequired,
  'requiredFieldErrors': PropTypes.oneOfType([
    PropTypes.instanceOf(OrderedMap),
    PropTypes.instanceOf(Map),
  ]).isRequired,
  'name': PropTypes.string,
  'shouldScrollToError': PropTypes.bool,
  'braintreeFieldStateChange': PropTypes.func.isRequired,
  'labelComponent': PropTypes.element,
  'defaultValue': PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  'data-field-name': PropTypes.string,
  'data-braintree-name': PropTypes.string,
  'trackEvent': PropTypes.func,
};
propTypes['data-field-name'] = PropTypes.string.isRequired;

const defaultProps = {
  'name': null,
  'defaultValue': null,
  'className': null,
  'labelComponent': null,
  'labelClassName': null,
  'shouldScrollToError': false,
  'placeholderText': null,
  'inputType': 'text',
  'data-field-name': null,
  'data-braintree-name': null,
  'trackEvent': () => {},
};

class BraintreeField extends React.Component {
  constructor(props) {
    super(props);

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.state = {
      value: '',
    };
  }

  componentWillMount() {
    if (this.props.defaultValue) {
      this.setState({ value: this.props.defaultValue.toString() });
      this.props.onFieldEvent({ target: { value: this.props.defaultValue.toString(), dataFieldName: this.props['data-field-name'] } });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.defaultValue) { return; }

    const newInputValue = nextProps.defaultValue.toString();
    if (!this.props.defaultValue) {
      this.setState({ value: newInputValue });
      this.props.onFieldEvent({ target: { value: newInputValue, dataFieldName: nextProps['data-field-name'] } });
    }
  }

  get analyticsName() {
    switch (this.props['data-field-name']) {
      case 'number':
        return 'CardNumber';
      case 'expirationMonth':
        return 'ExpirationMonth';
      case 'expirationYear':
        return 'ExpirationYear';
      case 'cvv':
        return 'CVV';
      case 'postalCode':
        return 'ZipCode';
      default:
        return '';
    }
  }

  handleFieldChange(e) {
    this.setState({ value: e.target.value });
    this.props.onFieldEvent(e);
    this.trackEvent(INPUT_CHANGE);
  }

  trackEvent(event) {
    const { field, analyticsName: name } = this;
    const { trackEvent } = this.props;

    trackEvent({
      ...event,
      name,
      properties: {
        required: 'true',
        filled: (!isEmpty(get(field, 'value', ''))).toString(),
      },
    });
  }

  handleVisibilityChange(visible) {
    if (visible) {
      this.trackEvent(INPUT_IMPRESSION);
    }
  }

  handleFocus() {
    this.trackEvent(INPUT_FOCUS);
  }

  handleClick() {
    this.trackEvent(INPUT_CLICK);
  }

  render() {
    const {
      id,
      name,
      className,
      labelClassName,
      labelComponent,
      requiredFields,
      requiredFieldErrors,
      shouldScrollToError,
      setShouldScrollToError,
      modifyRequiredFieldList,
      braintreeFieldStateChange,
      placeholderText,
      inputType,
    } = this.props;

    // The VisibilitySensor cannot sit between RequiredField
    // and input. The input MUST be the direct child of
    // the validation wrapper.
    return (
      <div className={className}>
        <label htmlFor={id} className={labelClassName}>
          {name}
          &nbsp;{labelComponent}
        </label>
        <VisibilitySensor onChange={this.handleVisibilityChange} partialVisibility>
          <RequiredField
            requiredFields={requiredFields}
            requiredFieldErrors={requiredFieldErrors}
            shouldScrollToError={shouldScrollToError}
            setShouldScrollToError={setShouldScrollToError}
            modifyRequiredFieldList={modifyRequiredFieldList}
            braintreeFieldStateChange={braintreeFieldStateChange}
          >
            <input
              value={this.state.value}
              onChange={this.handleFieldChange}
              id={id}
              data-field-name={this.props['data-field-name']}
              data-braintree-name={this.props['data-braintree-name']}
              className="braintree-field form-control"
              placeholder={placeholderText}
              type={inputType}
              onClick={this.handleClick}
              onFocus={this.handleFocus}
              ref={(field) => { this.field = field; }}
            />
          </RequiredField>
        </VisibilitySensor>
      </div>
    );
  }
}

BraintreeField.propTypes = propTypes;
BraintreeField.defaultProps = defaultProps;

export default BraintreeField;
